// Tuile du multiviewer : image, état, et sous l'image la bande d'identification (UMD) comme sur un mur d'écrans.
import type { Alarm, InputView } from "../api/types";
import { formatBitrate, formatNumber, inputHealth, PROTOCOL_LABEL } from "../lib/format";
import { serverText, t } from "../lib/i18n";
import { href } from "../lib/router";
import { Sparkline } from "./Sparkline";
import { HEALTH_BORDER, InputHealthPill } from "./status";
import { cx, Tag } from "./ui";

export function Thumbnail({ input, className }: { input: InputView; className?: string }) {
  const online = input.live.online;
  return (
    <div className={cx("relative aspect-video w-full overflow-hidden bg-black", className)}>
      {input.thumbnail_ts ? (
        <img src={`/api/inputs/${encodeURIComponent(input.id)}/thumbnail?t=${input.thumbnail_ts}`} alt=""
          className={cx("size-full object-cover", !online && "opacity-30 grayscale")} />
      ) : null}
      {!online && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-cond text-sm font-semibold uppercase tracking-[0.2em] text-err/90">
            {input.enabled ? t("Pas de signal") : t("Désactivée")}
          </span>
        </div>
      )}
      {online && !input.thumbnail_ts && (
        <div className="absolute inset-0 flex items-center justify-center text-[12px] text-faint">{t("Image en préparation…")}</div>
      )}
    </div>
  );
}

/** Défauts du contenu vus par l'analyse de la gateway (image noire, figée, son coupé), sur l'image. */
function ContentBadges({ input }: { input: InputView }) {
  const c = input.live.content;
  if (!input.live.online || !c) return null;
  const flags = [c.black && t("Image noire"), c.frozen && t("Image figée"), c.silent && t("Silence")].filter(Boolean) as string[];
  if (!flags.length) return null;
  return (
    <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
      {flags.map((f) => (
        <span key={f} className="rounded bg-err/85 px-1.5 py-px font-cond text-[11px] font-semibold uppercase tracking-wide text-white">{f}</span>
      ))}
    </div>
  );
}

/** Balayage : « Entrelacé » (détecté par l'analyse), ou la conversion en progressif et son état. */
export function ScanTag({ input }: { input: InputView }) {
  const conv = input.live.conversion;
  if (input.deinterlace !== "off") {
    const label = input.deinterlace === "field" ? t("Progressif 50p") : t("Progressif 25p");
    const ok = conv?.state === "running";
    return (
      <span className={cx("font-mono text-[12px]", ok ? "text-muted" : "text-warn")}
        title={ok ? t("Source entrelacée convertie en progressif par la gateway") : t("Conversion : {state}", { state: conv?.state ?? t("en attente") })}>
        {label}{ok ? "" : ` · ${conv?.state === "retrying" ? t("en échec") : t("en attente")}`}
      </span>
    );
  }
  if (input.live.online && input.live.content?.interlaced) {
    return (
      <span className="font-mono text-[12px] text-warn"
        title={t("Source entrelacée : la preview du navigateur peut rester noire. Dans l'entrée, « Source entrelacée » : convertir en progressif.")}>
        {t("Entrelacé")}
      </span>
    );
  }
  return null;
}

/** « Images B » détectées : le WebRTC des navigateurs ne les affiche pas, la preview passe en HLS. */
export function BFramesTag({ input }: { input: InputView }) {
  if (!input.live.online || input.live.content?.b_frames !== true) return null;
  return (
    <span className="font-mono text-[12px] text-muted"
      title={t("La vidéo contient des images B : le navigateur ne les affiche pas en temps réel (WebRTC), la preview passe en HLS (2 à 5 s de retard). Les sorties ne sont pas concernées. Pour une preview instantanée : désactiver les images B sur l'encodeur.")}>
      {t("Images B · preview HLS")}
    </span>
  );
}

/** Niveau sonore court terme (EBU R128) : repère -23 LUFS en diffusion, -70 = silence. */
export function Loudness({ value }: { value: number | null | undefined }) {
  if (value == null) return null;
  const quiet = value <= -70;
  return (
    <span className={cx("num font-mono text-[12px]", quiet ? "text-warn" : "text-muted")}
      title={t("Niveau sonore court terme (EBU R128, moyenne sur 3 s). Repère en diffusion : -23 LUFS.")}>
      {t("Son")} {quiet ? t("silence") : formatNumber(value, "LUFS")}
    </span>
  );
}

export function InputTile({ input, alarms, history }: { input: InputView; alarms: Alarm[]; history: number[] }) {
  const health = inputHealth(input, alarms);
  const srt = input.live.srt;
  const alarm = alarms.find((a) => a.source === `input:${input.id}`);
  return (
    <a href={href({ page: "input", id: input.id })}
      className={cx("group flex flex-col overflow-hidden rounded-md border border-line border-t-4 bg-surface",
        "transition-colors hover:border-faint", HEALTH_BORDER[health])}>
      <div className="relative">
        <Thumbnail input={input} />
        <div className="absolute top-2 left-2 flex gap-1">
          <span className="rounded bg-black/70 px-1.5 py-px font-mono text-[11px] text-ink uppercase">
            {PROTOCOL_LABEL[input.protocol]} · {input.mode}
          </span>
        </div>
        <ContentBadges input={input} />
      </div>
      <div className="flex items-center justify-between gap-2 border-b border-line bg-raised px-3 py-1.5">
        <span className="truncate font-cond text-[15px] font-semibold uppercase tracking-wide">{input.name}</span>
        <InputHealthPill input={input} alarms={alarms} />
      </div>
      <div className="flex flex-col gap-2 px-3 py-2.5">
        <div className="flex items-end justify-between gap-3">
          <div className="flex flex-col">
            <span className="num text-[15px] font-medium">{formatBitrate(input.live.bitrate_in_kbps)}</span>
            <span className="font-mono text-[11px] text-faint">{input.id}</span>
          </div>
          <Sparkline values={history} className="h-8 w-28" color={health === "degraded" ? "var(--color-warn)" : undefined} />
        </div>
        {input.live.online && <Loudness value={input.live.content?.loudness_lufs} />}
        <ScanTag input={input} />
        <BFramesTag input={input} />
        {srt && (
          <div className="num flex gap-4 font-mono text-[12px] text-muted">
            <span>RTT {formatNumber(srt.rtt_ms, "ms")}</span>
            <span className={srt.loss_pct > 0 ? "text-warn" : undefined}>{t("Pertes")} {formatNumber(srt.loss_pct, "%")}</span>
            <span>{t("Lien")} {formatNumber(srt.link_mbps, "Mb/s")}</span>
          </div>
        )}
        {input.live.online && (input.live.tracks?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-1">{input.live.tracks.map((track) => <Tag key={track} caps={false}>{track}</Tag>)}</div>
        )}
        {alarm && <p className="text-[12px] text-warn">{serverText(alarm.message)}</p>}
      </div>
    </a>
  );
}
